import type { DutchiePosLoyaltyConfig, FindOrCreateReferralCodeMutation } from 'src/gql/graphql';
import type { LoyaltyEnrollmentSurface } from 'src/utils/analytics/events';

export enum Statuses {
  none = 'none',
  loading = 'loading',
  signup = 'signup',
  terms = 'terms',
  pin = 'pin',
  incorrectPin = 'incorrectPin',
  verificationRequired = 'verificationRequired',
  success = 'success',
  notOptedInRedemptionError = 'notOptedInRedemptionError',
  isOptedInRedemptionError = 'isOptedInRedemptionError',
}

export enum Integrations {
  none = 'none',
  alpineiq = 'alpineiq',
  springbig = 'springbig',
  dutchiepos = 'dutchiepos',
}

export const verificationRequiredPrompts = {
  phone_unverified: 'Please verify your phone number to complete enrollment.',
  phone_not_verifiable: 'Please verify your phone number to complete enrollment.',
  phone_missing: 'Add a verified phone number to your account to complete enrollment.',
} as const;

export type VerificationRequiredCode = keyof typeof verificationRequiredPrompts;

type GraphQLErrorLike = {
  extensions?: {
    code?: unknown;
    errors?: Array<{ meta?: { code?: unknown } }>;
  };
};

const isVerificationRequiredCode = (code: unknown): code is VerificationRequiredCode =>
  typeof code === 'string' && code in verificationRequiredPrompts;

export const getVerificationRequiredPrompt = (error: unknown): string | undefined => {
  if (!error || typeof error !== 'object') {
    return undefined;
  }

  const apolloError = error as { graphQLErrors?: GraphQLErrorLike[]; errors?: GraphQLErrorLike[] };
  const graphQLErrors = apolloError.graphQLErrors ?? apolloError.errors ?? [];

  const code = graphQLErrors
    .flatMap(({ extensions }) => [extensions?.code, ...(extensions?.errors?.map(({ meta }) => meta?.code) ?? [])])
    .find(isVerificationRequiredCode);

  return code ? verificationRequiredPrompts[code] : undefined;
};

export type EnrollmentRequiresPhoneVerificationInput = {
  loyaltyConfig: DutchiePosLoyaltyConfig | null | undefined;
  phoneVerifiedAt: string | null | undefined;
  isEnrollmentPhoneVerificationGateEnabled: boolean;
};

export function getEnrollmentRequiresPhoneVerification({
  loyaltyConfig,
  phoneVerifiedAt,
  isEnrollmentPhoneVerificationGateEnabled,
}: EnrollmentRequiresPhoneVerificationInput): boolean {
  return (
    isEnrollmentPhoneVerificationGateEnabled &&
    // Fall back to the deprecated `OnWeb` field when `OnEcomm` is null/undefined (e.g. an
    // Armageddon rollout that hasn't reached this dispensary/env yet, or a request that hit an
    // apps/api instance ahead of Armageddon's deploy). Removes the cross-repo deploy-order
    // dependency for this gate -- see FINTECH-4782 / ECOMM-13527.
    !!(
      loyaltyConfig?.requireVerifiedPhoneForEnrollmentOnEcomm ?? loyaltyConfig?.requireVerifiedPhoneForEnrollmentOnWeb
    ) &&
    !phoneVerifiedAt
  );
}

export type DutchiePosLoyaltyEnrollmentEligibilityInput = {
  showLoyaltyRewards: boolean;
  loyaltyProgram: Integrations | string;
  loyaltyConfig: DutchiePosLoyaltyConfig | null | undefined;
  isLoyaltyMember: boolean | null | undefined;
  phoneVerifiedAt?: string | null | undefined;
  isEnrollmentPhoneVerificationGateEnabled?: boolean;
};

export function getIsDutchiePosLoyaltyEnrollmentEligible({
  showLoyaltyRewards,
  loyaltyProgram,
  loyaltyConfig,
  isLoyaltyMember,
  phoneVerifiedAt,
  isEnrollmentPhoneVerificationGateEnabled = false,
}: DutchiePosLoyaltyEnrollmentEligibilityInput): boolean {
  if (
    !showLoyaltyRewards ||
    loyaltyProgram !== Integrations.dutchiepos ||
    !loyaltyConfig?.loyaltyRequireOptIn ||
    isLoyaltyMember !== false
  ) {
    return false;
  }

  const enrollmentRequiresPhoneVerification = getEnrollmentRequiresPhoneVerification({
    loyaltyConfig,
    phoneVerifiedAt,
    isEnrollmentPhoneVerificationGateEnabled,
  });

  return !enrollmentRequiresPhoneVerification;
}

export type ShowErnie = (
  msg?: string,
  type?: 'danger' | 'error' | 'info' | 'success',
  timeout?: number,
  reasonCode?: string
) => void;

export type UseLoyaltyProps = {
  initialStatus: Statuses;
  integration: Integrations;
  surface: LoyaltyEnrollmentSurface | null;
  referralData?: FindOrCreateReferralCodeMutation['findOrCreateReferralCode'] | null;
};

export type UseLoyaltyReturn = {
  createUserAndPin: () => Promise<void>;
  status: Statuses;
  setStatus: (status: Statuses) => void;
  programName: string;
  programDescription: string;
  processPin: (pin: string) => Promise<void>;
  resendPin: () => Promise<void>;
};
